import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { jobApi } from '../../api/jobApi'
import { adminApi } from '../../api/adminApi'
import { ReferralFeePreset } from '../../types'
import { toast } from 'react-hot-toast'
import { X, Plus, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

const schema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  location: z.string().optional(),
  jobType: z.string().optional(),
  experienceMin: z.number().min(0).max(50).optional(),
  experienceMax: z.number().min(0).max(50).optional(),
  referralFeePaise: z.number().min(0),
})

type FormValues = z.infer<typeof schema>

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Remote', 'Hybrid']

export default function CreateJobPage() {
  const navigate = useNavigate()
  const [feePresets, setFeePresets] = useState<ReferralFeePreset[]>([])
  const [skills, setSkills] = useState<string[]>([])
  const [skillInput, setSkillInput] = useState('')
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { referralFeePaise: 0 },
  })

  const selectedFee = watch('referralFeePaise')

  useEffect(() => {
    adminApi.getFeePresets().then(({ data }) => setFeePresets(data.data || []))
  }, [])

  const addSkill = () => {
    const s = skillInput.trim()
    if (s && !skills.includes(s) && skills.length < 20) {
      setSkills([...skills, s])
      setSkillInput('')
    }
  }

  const removeSkill = (skill: string) => setSkills(skills.filter((s) => s !== skill))

  const onSubmit = async (values: FormValues) => {
    setLoading(true)
    try {
      await jobApi.create({ ...values, skills })
      toast.success('Job posted! It will be visible after admin approval.')
      navigate('/my-jobs')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create job')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 no-underline">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="card">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Post a Job</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Title *</label>
            <input {...register('title')} className="input" placeholder="e.g. Senior Software Engineer" />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>

          {/* Location + Job Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Location</label>
              <input {...register('location')} className="input" placeholder="e.g. Bangalore, Remote" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Job Type</label>
              <select {...register('jobType')} className="input">
                <option value="">Select type</option>
                {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Experience */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Experience (yrs)</label>
              <input
                type="number"
                {...register('experienceMin', { valueAsNumber: true })}
                className="input"
                min={0}
                max={50}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Experience (yrs)</label>
              <input
                type="number"
                {...register('experienceMax', { valueAsNumber: true })}
                className="input"
                min={0}
                max={50}
                placeholder="10"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
            <textarea
              {...register('description')}
              rows={6}
              className="input resize-none"
              placeholder="Describe the role, responsibilities, and requirements..."
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description.message}</p>}
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Skills</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                className="input flex-1"
                placeholder="Type a skill and press Enter"
              />
              <button type="button" onClick={addSkill} className="btn-secondary px-3">
                <Plus size={16} />
              </button>
            </div>
            {skills.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {skills.map((skill) => (
                  <span key={skill} className="badge badge-gray flex items-center gap-1">
                    {skill}
                    <button type="button" onClick={() => removeSkill(skill)} className="ml-0.5">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Referral Fee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Referral Fee</label>
            <div className="flex flex-wrap gap-2">
              {feePresets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setValue('referralFeePaise', preset.amountPaise)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedFee === preset.amountPaise
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-primary-300'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {errors.referralFeePaise && (
              <p className="text-xs text-red-500 mt-1">{errors.referralFeePaise.message}</p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              {selectedFee === 0
                ? 'Free referral — no charge to job seekers'
                : `Job seekers will be charged ₹${selectedFee / 100} (held in escrow)`}
            </p>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Posting...' : 'Post Job'}
          </button>
        </form>
      </div>
    </div>
  )
}
